// Run-scoped harness transport — carved from `src/features/agent-harness/client.ts`
// (ARCFE-1059 · D1). Transport comes from the `@heidi/api` configured-client
// registry (E5, ARCFE-805); bodies are otherwise identical to the pre-move
// source, `scribeHttpV2.` -> `http()`.
//
// A MIXED CARVE, deliberately. `RunTransport` has eight members and only these
// five are pure HTTP over the run paths. The other three — `streamPath`,
// `eventsPath`, `streamHeaders` — resolve the ml-scribe origin
// (`@/helpers/getDomains`) and mint auth/identity headers
// (`@/utils/http/clientIdentityHeaders`, `getAuthTokenForHttpClientSync`),
// which are app and platform state a headless package may not hold. So the app
// keeps `client.ts` as the ASSEMBLY: it imports these five, adds the three it
// owns, and exports the same `agentHarnessClient` object with the same shape.
// That is the commerce mixed-shim precedent, not a seam — nothing is injected.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  ApprovalMode,
  AttachmentDownloadUrlResponse,
  CancelTurnReason,
  PublicRunSnapshot,
  ResolveApprovalRequest,
  ResolveApprovalResponse,
  SubmitTurnRequest,
  SubmitTurnResponse,
} from '../../contracts/harness.types';
import {
  agentHarnessAttachmentDownloadUrlPath,
  agentHarnessRunApprovalModePath,
  agentHarnessRunApprovalResolvePath,
  agentHarnessRunCancelPath,
  agentHarnessRunPath,
  agentHarnessRunTurnsPath,
} from '../../contracts/paths';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

export const getRun = async (agentRunId: string): Promise<PublicRunSnapshot> => {
  const response = await http().get<PublicRunSnapshot>(agentHarnessRunPath(agentRunId));
  return response.data;
};

export const submitTurn = async (
  agentRunId: string,
  body: SubmitTurnRequest
): Promise<SubmitTurnResponse> => {
  const response = await http().post<SubmitTurnResponse>(
    agentHarnessRunTurnsPath(agentRunId),
    body
  );
  return response.data;
};

export const cancelTurn = async (
  agentRunId: string,
  reason?: CancelTurnReason
): Promise<void> => {
  await http().post(agentHarnessRunCancelPath(agentRunId), reason ? { reason } : {});
};

// Applies from the run's next tool call, mid-turn.
export const setAgentRunApprovalMode = async (
  agentRunId: string,
  approvalMode: ApprovalMode
): Promise<ApprovalMode> => {
  const response = await http().patch<{ approval_mode: ApprovalMode }>(
    agentHarnessRunApprovalModePath(agentRunId),
    { approval_mode: approvalMode }
  );
  return response.data.approval_mode;
};

export const resolveApproval = async (
  agentRunId: string,
  approvalId: string,
  body: ResolveApprovalRequest
): Promise<ResolveApprovalResponse> => {
  const response = await http().post<ResolveApprovalResponse>(
    agentHarnessRunApprovalResolvePath(agentRunId, approvalId),
    body
  );
  return response.data;
};

export const attachmentDownloadUrl = async (
  agentRunId: string,
  attachmentId: string
): Promise<AttachmentDownloadUrlResponse> => {
  const response = await http().get<AttachmentDownloadUrlResponse>(
    agentHarnessAttachmentDownloadUrlPath(agentRunId, attachmentId)
  );
  return response.data;
};
